type ReorderableStage = {
  // type does not matter, all we check is whether guard is null or not
  guard?: unknown;
};

type StageReorderValidation =
  | { valid: true }
  | { valid: false; reason: string };

/**
 * Validates that reordering a pipeline stage does not result in a guarded
 * stage becoming the entry (first) stage in the pipeline.
 *
 * @param stages - All stages in pipeline order (index 0 = entry stage)
 * @param fromIndex - Current position of the stage being moved
 * @param toIndex - Target position for the stage
 */
export function validateStageReorder(
  stages: readonly ReorderableStage[],
  fromIndex: number,
  toIndex: number
): StageReorderValidation {
  if (fromIndex === toIndex) return { valid: true };

  const stageBeingMoved = stages[fromIndex];

  // A guarded stage cannot become the entry stage
  if (toIndex === 0 && stageBeingMoved.guard !== null) {
    return {
      valid: false,
      reason:
        'Cannot move a stage with a guard to the first position in the pipeline',
    };
  }

  // Moving the entry stage away when the next stage has a guard would make the
  // guarded stage the new entry stage
  if (fromIndex === 0 && stages[1]?.guard !== null) {
    return {
      valid: false,
      reason: 'Cannot move the first stage when the second stage has a guard',
    };
  }

  return { valid: true };
}
